import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import { Box, Stack, TextField, Typography } from '@mui/material'
import { debounce } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { LuX } from 'react-icons/lu'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import editIcon from '@/assets/images/BlueEdit.svg'
import downloadIcon from '@/assets/images/DownloadBlueIcon.svg'
import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import Icon from '@/components/Icon'
import IconText from '@/components/IconText'
import { useAppSelector } from '@/hooks/redux'
import {
  useDeleteSavedRecipeMutation,
  useUpdateRecipeNameMutation,
} from '@/store/apiService'
import { clearSelectedRecipeIds } from '@/store/project'
import { getProjectPermissions } from '@/utils/constants'

interface ProjectsToolbarProps {
  selectedRows: number[]
  refetch: () => void
  setSearch: (value: string) => void
  caseId: string
  caseData: any
  caseDetails: any
}

const RecipeDetailToolbar = ({
  selectedRows,
  refetch,
  setSearch,
  caseId,
  caseData,
  caseDetails,
}: ProjectsToolbarProps) => {
  const dispatch = useDispatch()

  const [deleteSaveRecipe, { isLoading: isDeletingRecipe }] =
    useDeleteSavedRecipeMutation()
  const [updateRecipeName, { isLoading: isRenamingRecipe }] =
    useUpdateRecipeNameMutation()

  const [isDeleteDialog, setIsDeleteDialog] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const selectedImageIds = useAppSelector(
    (state) => state?.project?.selectedRecipeIds,
  )

  const [form, setForm] = useState({ recipeName: '' })

  const handleEdit = () => {
    if (caseData.length === 1) {
      const selectedRecipe = caseData[0]
      setForm({ recipeName: selectedRecipe.name })
      setEditOpen(true)
    }
  }

  const handleUpdate = async () => {
    try {
      await updateRecipeName({
        id: selectedImageIds,
        name: form.recipeName,
        caseId: caseId,
      }).unwrap()
      toast.success('Recipe renamed successfully.')
      dispatch(clearSelectedRecipeIds())
      setEditOpen(false)
      refetch()
    } catch (err) {
      toast.error('Failed to rename recipe.')
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value)
      }, 500),
    [setSearch],
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetSearch(e.target.value)
  }

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel()
    }
  }, [debouncedSetSearch])

  const isFormValid = form.recipeName.trim() !== ''
  const role = caseDetails?.role
  const permissions = getProjectPermissions(role)

  const handleRecipeDownload = async (recipes: any[]) => {
    if (!recipes?.length) return

    if (recipes.length === 1) {
      const url = recipes[0]?.recipe_pdf
      if (url) {
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        link.remove()
      }
    } else {
      const JSZip = (await import('jszip')).default
      const zip = new JSZip()

      const promises = recipes.map(async (recipe) => {
        try {
          const response = await fetch(recipe.recipe_pdf)
          const blob = await response.blob()

          const filename = `${recipe.name}.pdf`

          zip.file(filename, blob)
        } catch (error) {
          console.error(`Failed to fetch PDF for recipe ${recipe.id}`, error)
        }
      })

      await Promise.all(promises)

      const content = await zip.generateAsync({ type: 'blob' })
      const blobUrl = URL.createObjectURL(content)

      const link = document.createElement('a')
      link.href = blobUrl
      link.download = 'recipes.zip'
      link.click()
      URL.revokeObjectURL(blobUrl)
    }

    dispatch(clearSelectedRecipeIds())
  }

  return (
    <>
      <Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={
            selectedRows?.length > 0 ? 'space-between' : 'flex-end'
          }
          spacing={2}
          py={3}
        >
          {selectedRows?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: '#464646',
                borderRadius: '4px',
                px: 2,
                py: 1,
                flexGrow: 1,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography
                  sx={{ color: 'white', fontSize: '14px', fontWeight: 400 }}
                >
                  {selectedRows?.length} selected:
                </Typography>

                {selectedRows?.length === 1 && permissions.canEditRecipes && (
                  <IconText
                    icon={<img src={editIcon} alt="" />}
                    label="Rename"
                    onClick={handleEdit}
                  />
                )}

                {permissions.canDownloadRecipes && (
                  <IconText
                    icon={<img src={downloadIcon} alt="" />}
                    label="Download"
                    onClick={async () => handleRecipeDownload(caseData)}
                  />
                )}

                {permissions.canDeleteRecipes && (
                  <IconText
                    icon={<DeleteIcon />}
                    label="DELETE"
                    onClick={() => setIsDeleteDialog(true)}
                  />
                )}
              </Stack>

              <Icon
                icon={LuX}
                size={18}
                onClick={() => dispatch(clearSelectedRecipeIds())}
              />
            </Box>
          )}

          <TextField
            size="small"
            placeholder="Search"
            variant="outlined"
            InputProps={{
              endAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
            }}
            sx={searchFieldStyle}
            onChange={handleSearchChange}
          />
        </Stack>
      </Stack>

      <Dialog
        isOpen={isDeleteDialog}
        onClose={() => setIsDeleteDialog(false)}
        title="Delete Recipe"
        content={
          <p>
            Are you sure you want to remove {selectedImageIds?.length} recipe
            from the project?
            <br />
            This action cannot be undone.
          </p>
        }
        paperStyles={{ width: '600px' }}
        actionButtons={
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              label="CANCEL"
              onClick={() => {
                setIsDeleteDialog(false)
              }}
            />
            <Button
              variant="contained"
              label="DELETE"
              onClick={async () => {
                if (
                  caseId &&
                  selectedImageIds &&
                  permissions.canDeleteRecipes
                ) {
                  try {
                    await deleteSaveRecipe({
                      ids: [selectedImageIds],
                      caseId: caseId,
                    }).unwrap()
                    setIsDeleteDialog(false)
                    dispatch(clearSelectedRecipeIds())
                    toast.success('Recipe deleted successfully.')
                    refetch()
                  } catch (err) {
                    toast.error('Failed to delete recipe.')
                  }
                }
              }}
              loading={isDeletingRecipe}
              disabled={isDeletingRecipe}
            />
          </Box>
        }
        triggerElement={undefined}
      />

      <Dialog
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        title={
          <Typography fontSize={24} fontWeight={500}>
            Rename Recipe
          </Typography>
        }
        content={
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Recipe name"
              name="recipeName"
              value={form.recipeName}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="medium"
            />
          </Box>
        }
        actionButtons={
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              label="CANCEL"
              onClick={() => setEditOpen(false)}
              variant="outlined"
            />
            <Button
              label="SAVE"
              onClick={handleUpdate}
              variant="contained"
              disabled={!isFormValid || isRenamingRecipe}
              loading={isRenamingRecipe}
            />
          </Box>
        }
        paperStyles={{ width: '800px' }}
        triggerElement={null}
      />
    </>
  )
}

const searchFieldStyle = {
  input: { color: 'white' },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
}

export default RecipeDetailToolbar
